import { useState } from 'react';
import useNavigationStore from '@src/stores/navigationStore';
import useFilesStore from '@src/stores/filesStore';

import styles from './ProofOfAddress.module.scss';

import BlockTitle from '@src/components/_shared/UI/BlockTitle';
import Container from '@src/components/_shared/Container';
import Subtitle from '@src/components/_shared/UI/Subtitle';
import ValidateButton from '@src/components/_shared/UI/ValidateButton';
import FileUploadInput from '@src/components/_shared/UI/FileUploadInput';
import { DocumentLabelType } from '@src/utilities/types';
import toast from 'react-hot-toast';
import { MAXSIZEFILE } from '@src/utilities/constants';
import useRegistrationStore from '@src/stores/registrationStore';
import { useMutation } from '@tanstack/react-query';
import { fileCheck } from '@src/api/Registration';
import InfoBlueBox from '@src/components/_shared/InfoBlueBox';
import ConfirmModal from '@src/components/_shared/ConfirmModal';

export default function ProofOfAddress() {
    const { setBlock } = useNavigationStore();
    const { registration } = useRegistrationStore();
    const { files, addLegalRepresentativeFile, removeLegalRepresentativeFile } = useFilesStore();
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [refreshKey, setRefreshKey] = useState<number>(0);
    const [uploadError, setUploadError] = useState<string | null>(null);

    const proofOfAddressFile = files.person.legalRepresentative.find((file) => file.type === 'address');

    const { mutate, isPending } = useMutation({
        mutationFn: (addressFormData: FormData) => fileCheck('address', addressFormData),
        onSuccess: () => {
            if (registration.company.beneficial_owners.length > 1) {
                setBlock('BeneficiariesDocuments');
            } else {
                setBlock('CompanyDocuments');
            }
        },
        onError: (error: any) => {
            let errorMessage = `Une erreur est survenue lors de la validation du document.`;
            if (error?.response?.status === 400) {
                errorMessage = 'Le document est invalide. Veuillez en sélectionner un autre.';
            }
            if (error?.response?.status === 409) {
                errorMessage = 'Ce document existe déjà. Veuillez en sélectionner un autre.';
            }
            setUploadError(errorMessage);
        }
    });

    const _handleSelectFile = (id: number, file: File) => {
        const allowedTypes = ['application/pdf', 'image/png', 'image/jpeg'];
        if (!allowedTypes.includes(file.type)) {
            return toast.error('Format de fichier non supporté. Veuillez choisir un fichier PDF ou une image (.png, .jpeg).', { position: 'top-center'})
        }
        if (file.size > MAXSIZEFILE) {
            return toast.error('Ce fichier est trop volumineux. La taille maximum est de 10 Mo.', { position: 'top-center'});
        }
        addLegalRepresentativeFile({
            id: id,
            fileName: file.name,
            fileSize: file.size,
            file: file,
            type: 'address',
            type_detail: '',
            label: file.type === 'application/pdf' ? DocumentLabelType.pdf : DocumentLabelType.image
        });
        setRefreshKey((prev) => prev + 1);
    };

    const _handleDeleteFile = (id: number) => {
        removeLegalRepresentativeFile(id);
        setRefreshKey((prev) => prev + 1);
        setUploadError(null);
    };

    const _onValidate = () => {
        if (!proofOfAddressFile?.file || !(proofOfAddressFile.file instanceof File)) {
            toast.error("Le fichier recto téléchargé est invalide. Veuillez réessayer.");
            return;
        }
        const addressFormData = new FormData();
        addressFormData.append('file', proofOfAddressFile.file);
        addressFormData.append('demand_id', registration.demand_id);
        addressFormData.append('fromWeb', 'true');
        addressFormData.append('registrationType', 'company');
        if (registration.partner_informations.is_partner) addressFormData.append('is_psi', 'true');
        if (registration.person_id) addressFormData.append('person_id', registration.person_id);
        if (registration.partner_informations.brand.slug) addressFormData.append('brand', registration.partner_informations.brand.slug);

        mutate(addressFormData);
    };

    const _confirmDeleteFile = () => {
        setIsModalOpen(false);
        _handleDeleteFile(3);
    };

    const _cancelDeleteFile = () => {
        setIsModalOpen(false);
    };

    return (
        <Container>
            <BlockTitle title={`Justificatif de domicile de ${registration.person.basic.civility} ${registration.person.basic.firstname} ${registration.person.basic.lastname}, représentant légal`} />
            <Subtitle content="Télécharger un justificatif de domicile pour compléter votre inscription et commencer à investir" />
            <p className={styles.content}>Fichiers acceptés : .pdf, images (.jpeg, .png)</p>
            <InfoBlueBox
                boxTitle="Important"
                description="Le justificatif doit être à votre nom et dater de moins de trois mois. Sont acceptées : facture d'électricité, eau, gaz, téléphone ou internet."
            />
            <FileUploadInput
                key={`proofOfAddress-${refreshKey}`}
                title={'Télécharger un document'}
                documentName={proofOfAddressFile?.fileName}
                isValidated={!!proofOfAddressFile?.fileName}
                selectFile={(file) => _handleSelectFile(3, file)}
                deleteFile={() => setIsModalOpen(true)}
                initialFileSize={proofOfAddressFile?.fileSize}
                accept=".pdf,image/png,image/jpeg"
                hasError={!!uploadError}
                errorMessage={uploadError || ''}
            />

            {proofOfAddressFile?.fileName ? (
                <ValidateButton title="Continuer" onClick={_onValidate} disabled={isPending} onLoading={isPending} />
            ) : null}
            <ConfirmModal
                isOpen={isModalOpen}
                onConfirm={_confirmDeleteFile}
                onCancel={_cancelDeleteFile}
            />
        </Container>
    );
}
